// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// ⚠️⚠️ UM NÚMERO DE QUILÓMETROS ESCREVE-SE SEMPRE DA MESMA MANEIRA
// ============================================================================
//
// 📗 Cliente, 2026-09-10: *«nas consultas os kms estão com imensas casas decimais, valida
//    perfeitamente»*.
//
// ---------------------------------------------------------------------------
// A REGRA, e a razão de ela ser esta
// ---------------------------------------------------------------------------
//
//   **Um número de quilómetros mostra-se arredondado à unidade e com separador de milhares
//   de pt-PT.**
//
//   Arredonda-se à unidade porque **a casa decimal não existe na origem do número**: todos os
//   quilómetros deste sistema saem de `kmAproximados` — a distância de círculo máximo entre
//   duas coordenadas (`12742 · asin(√m)`, `motor-fases.ts`). É uma aproximação por linha recta
//   sobre estrada real: a diferença contra a estrada é de **dezenas de quilómetros**, logo a
//   primeira casa decimal é ruído do vírgula-flutuante e não medição. Escrevê-la dá ao palpite
//   a aparência de uma medida — que é a família de defeito que este projecto persegue.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ PORQUE É QUE ISTO É UMA FUNÇÃO PARTILHADA E NÃO UM `Math.round` EM CADA SÍTIO
// ---------------------------------------------------------------------------
//
//   Porque já era. Medido a 2026-09-10, **a mesma expressão estava escrita quatro vezes**, cada
//   uma local ao seu ficheiro e nenhuma a saber das outras:
//
//     · `apps/web/src/mapa/Kpis.tsx`            → `const n = (v) => Math.round(v).toLocaleString('pt-PT')`
//     · `packages/dominio/src/motor-fases.ts`   → `const kmTexto = …` (dentro de uma função)
//     · `packages/dominio/src/custo-do-condutor.ts` → `const km = …`
//     · `apps/web/src/mapa/Consultas.tsx`       → duas vezes por extenso, em dois `<dd>`
//
//   Contado com o detector de `apps/web/src/mapa/km-no-ecra.test.ts`: **35 sítios** escreviam
//   quilómetros nos ecrãs, e **24 deles não arredondavam nada**. E o caso que fecha o argumento
//   é o `totaisDoLote.km` do `Consultas.tsx`: o **mesmo** número, arredondado num sítio e cru
//   130 linhas abaixo, no mesmo ecrã — com um comentário no primeiro a explicar que o cliente já
//   se tinha queixado dele. É a assinatura de «duas portas, uma fechada», a família que já custou
//   oito instâncias medidas a este projecto: corrigiu-se onde se viu, e onde não se viu ficou.
//   A regra 8 do `CLAUDE.md` manda **uma só formulação partilhada**, porque duas formulações
//   divergem e a que divergir é a que ninguém corre.
//
// ---------------------------------------------------------------------------
// ⚠️ O QUE ESTA FUNÇÃO **NÃO** FAZ, DE PROPÓSITO
// ---------------------------------------------------------------------------
//
//   **Não trata de `NaN` nem de `Infinity`.** `kmEntre` (`intervalos.ts`) devolve `NaN` quando
//   falta uma coordenada, e há sítios no motor que já se defendem disso com um
//   `Number.isFinite(km) ? km : 0`. 📌 Se esta função devolvesse `'?'` ou `'0'` por um `NaN`,
//   passava a **apagar** esse defeito em silêncio no exacto sítio onde ele se vê. Um `NaN` no
//   ecrã é feio e é honesto: diz que a distância não foi calculada. Quem sabe o que fazer com a
//   ausência é o chamador, que tem o contexto — e os chamadores que a podem ter já decidem antes
//   (`x === null ? '—' : kmParaOEcra(x)`).
//
//   **Não escreve a unidade.** O `km` fica no texto de quem chama, porque em muitos sítios ele
//   vem depois de outra coisa («309 km de MAD») ou dentro de um `<strong>` só com o número.
// ============================================================================
/**
 * Um número de quilómetros como o planeador o lê: arredondado à unidade, com separador de
 * milhares de pt-PT. Sem a unidade — ver a nota do cabeçalho.
 *
 * ⚠️ `Math.round` **antes** do `toLocaleString`, e não só o `toLocaleString`. Sem o
 * arredondamento, `Intl` corta a três casas por omissão: `1234.5678` sai `'1234,568'`, que
 * *parece* formatado e não está. Foi exactamente esse o defeito de `Consultas.tsx` em quatro
 * sítios, e é o que torna esta família difícil de ver a olho.
 *
 * ⚠️ **O agrupamento de pt-PT começa nos CINCO dígitos**, não nos mil (`min2`), e o separador é
 * `U+00A0`. Medido, não presumido — ver o cabeçalho de `km-no-ecra.test.ts`.
 *
 * @example kmParaOEcra(1234.5678)              // '1235'   (pt-PT não agrupa 4 dígitos)
 * @example kmParaOEcra(12345.678)              // '12 345' (o espaço é U+00A0)
 * @example kmParaOEcra(146.99999999999997)     // '147'
 */
export function kmParaOEcra(km) {
    return Math.round(km).toLocaleString('pt-PT');
}
//# sourceMappingURL=km-no-ecra.js.map